/**
 * Validation of API-provided Google Calendar authorization links before browser navigation.
 *
 * @module apps/web/src/lib/calendarOAuth
 */

/** Accepted Google Calendar authorization response variants used by the browser redirect helper. */
interface CalendarAuthorizationResponse {
  /** Whether the authorization URL request reports success. */
  success: boolean;
  /** Successful response payload containing the provider authorization URL. */
  data?: { /** Provider authorization URL validated by the caller before navigation. */ url?: string };
  /** Failure details returned when authorization URL creation fails. */
  error?: { /** User-facing explanation of the authorization failure. */ message?: string };
}

/**
 * Return an HTTPS accounts.google.com OAuth authorization URL with an accepted path and no embedded
 * credentials. Throws on API failure, missing/malformed URLs, or any other origin/path.
 */
export function calendarAuthorizationUrl(response: CalendarAuthorizationResponse): string {
  if (!response.success) {
    throw new Error(response.error?.message || 'Google Calendar could not start sign-in. Please try again.');
  }
  const invalid = 'Google Calendar did not return a valid sign-in link. Please try again.';
  if (!response.data?.url) throw new Error(invalid);
  let url: URL;
  try { url = new URL(response.data.url); } catch { throw new Error(invalid); }
  if (
    url.origin !== 'https://accounts.google.com' || url.username || url.password ||
    !['/o/oauth2/v2/auth', '/o/oauth2/auth'].includes(url.pathname)
  ) throw new Error(invalid);
  return url.href;
}
